/**
 * movementComponent — the avatar's client-side "brain" for navmesh movement
 * (ENG-592). Modeled on Unreal's CharacterMovementComponent +
 * PathFollowingComponent + AIController::MoveTo*.
 *
 * Module singleton (like animVariables): state lives here, NOT in React. A
 * single useFrame (NavMeshVolume) calls tick(dt) once per frame, which mutates
 * the avatar group's XZ position + yaw directly — no React state on the hot
 * path (CLAUDE.md hard rule). Y is owned by avatarFloor.
 *
 * THREE-free on purpose: movement math is plain trig so the component
 * unit-tests against a fake group ({ position, rotation }).
 */

const TWO_PI = Math.PI * 2

function shortestAngle(from, to) {
  let d = (to - from) % TWO_PI
  if (d > Math.PI) d -= TWO_PI
  if (d < -Math.PI) d += TWO_PI
  return d
}

class MovementComponent {
  constructor() {
    this.reset()
    this.cfg = {
      maxSpeed: 1.4,        // m/s (~human walk)
      accel: 6.0,           // m/s^2
      acceptanceRadius: 0.2,// m
      turnRate: 8.0,        // rad/s
      facingOffset: 0,      // added to computed yaw (model forward convention)
      replanThreshold: 0.5, // moveToActor re-paths if target drifts this far (m)
    }
    this._navmesh = null
    this._group = null
    this._eventSink = null
    this._speedSink = null
    this._walkAction = null
    this._walkPlaying = false
  }

  reset() {
    this.status = 'idle' // idle | moving | arrived | failed
    this.path = []       // [[x,y,z], ...]
    this.targetIndex = 0
    this.speed = 0
    this.goalKind = null // 'location' | 'actor' | 'random'
    this.goalRef = null
    this._goalPoint = null // final destination [x,y,z]
  }

  // --- wiring ---
  setNavMesh(nm) { this._navmesh = nm }
  setGroup(g) { this._group = g }
  /** The avatar's root group (THREE.Object3D) — "the character" for the
   *  Character→Transform blueprint nodes. Null until the avatar mounts. */
  getGroup() { return this._group }
  setEventSink(fn) { this._eventSink = fn } // (type, data) => void
  /** Called with the current ground speed (m/s), 0 when stopped — the blueprint gates
   *  its walk state on it. A sink, so this module stays THREE-free and testable. */
  setSpeedSink(fn) { this._speedSink = fn }
  configure(patch) { Object.assign(this.cfg, patch) }

  // --- public API (UE MoveTo*) ---
  moveToLocation(point, opts = {}) {
    const p = toVec3(point)
    if (!p) return this._fail('moveToLocation: bad point')
    this.goalKind = 'location'
    this.goalRef = null
    return this._planTo(p, opts)
  }

  moveToActor(target, opts = {}) {
    const p = this._resolveActor(target)
    if (!p) return this._fail('moveToActor: target not resolvable')
    this.goalKind = 'actor'
    this.goalRef = target
    return this._planTo(p, opts)
  }

  moveToRandomPoint(center, radius, opts = {}) {
    if (!this._navmesh) return this._fail('moveToRandomPoint: no navmesh')
    const c = toVec3(center) || this._currentPos()
    const p = this._navmesh.getRandomReachablePoint(c, radius, opts.rng)
    if (!p) return this._fail('moveToRandomPoint: no reachable point in radius')
    this.goalKind = 'random'
    this.goalRef = null
    return this._planTo(p, opts)
  }

  /** UE StopMovement — halt and clear the path. */
  abort() {
    if (this.status === 'moving') {
      this.reset()
      this._setWalk(false)
      this._fire('onMoveCompleted', { result: 'aborted' })
    } else {
      this.reset()
      this._setWalk(false)
    }
  }

  // --- per-frame ---
  tick(dt) {
    if (this.status !== 'moving' || !this._group) return
    if (!dt || dt <= 0) return

    // moveToActor: follow a possibly-moving target.
    if (this.goalKind === 'actor') {
      const p = this._resolveActor(this.goalRef)
      if (p && this._goalPoint && distXZ(p, this._goalPoint) > this.cfg.replanThreshold) {
        if (!this._planTo(p, {})) return
      }
    }

    const pos = this._currentPos()
    const finalGoal = this._goalPoint
    // Acceptance radius against the FINAL destination ends the move early.
    if (finalGoal && distXZ(pos, finalGoal) <= this.cfg.acceptanceRadius) {
      return this._arrive()
    }

    let wp = this.path[this.targetIndex]
    if (!wp) return this._arrive()

    // Accelerate toward max speed.
    this.speed = Math.min(this.cfg.maxSpeed, this.speed + this.cfg.accel * dt)
    let step = this.speed * dt

    // Advance through waypoints we overshoot this frame.
    let px = pos[0], pz = pos[2]
    while (wp) {
      const d = distXZ([px, 0, pz], wp)
      if (step < d) {
        const inv = d > 1e-9 ? step / d : 0
        px += (wp[0] - px) * inv
        pz += (wp[2] - pz) * inv
        step = 0
        break
      }
      // reach this waypoint, carry remaining step to the next
      px = wp[0]; pz = wp[2]
      step -= d
      this.targetIndex++
      wp = this.path[this.targetIndex]
    }

    // Face movement direction (yaw), rate-limited.
    const dirX = px - pos[0], dirZ = pz - pos[2]
    if (dirX * dirX + dirZ * dirZ > 1e-12) {
      const targetYaw = Math.atan2(dirX, dirZ) + this.cfg.facingOffset
      const cur = this._group.rotation.y
      const delta = shortestAngle(cur, targetYaw)
      const maxStep = this.cfg.turnRate * dt
      this._group.rotation.y = cur + Math.max(-maxStep, Math.min(maxStep, delta))
    }

    this._group.position.x = px
    this._group.position.z = pz
    this._setWalk(true)

    if (!wp) this._arrive()
  }

  // --- internals ---
  _planTo(goalPoint, opts) {
    if (!this._navmesh) return this._fail('no navmesh loaded')
    const from = this._currentPos()
    const path = this._navmesh.findPath(from, goalPoint)
    if (!path || path.length === 0) return this._fail('no path to goal')
    if (Number.isFinite(opts.acceptanceRadius)) this.cfg.acceptanceRadius = opts.acceptanceRadius
    this.path = path
    this.targetIndex = 0
    this._goalPoint = goalPoint.slice()
    this.status = 'moving'
    return true
  }

  _arrive() {
    // Stop where we are — we're already within acceptanceRadius of the goal.
    // Do NOT teleport to the exact goal: snapping the remaining (up to
    // acceptanceRadius) distance shows a visible jump on the final segment
    // (ENG-592). Unreal's acceptance radius means "arrived when within R", not
    // "teleport to center".
    this.status = 'arrived'
    this.speed = 0
    this.path = []
    this._setWalk(false)
    this._fire('onMoveCompleted', { result: 'success' })
  }

  _fail(reason) {
    this.status = 'failed'
    this.speed = 0
    this.path = []
    this._setWalk(false)
    this._fire('onMoveCompleted', { result: 'failed', reason })
    return false
  }

  _currentPos() {
    if (this._group) {
      const p = this._group.position
      return [p.x, p.y, p.z]
    }
    return [0, this._floorY(), 0]
  }

  _floorY() {
    return this._navmesh ? this._navmesh.floorY : 0
  }

  _resolveActor(target) {
    if (!target) return null
    if (Array.isArray(target)) return toVec3(target)
    if (typeof target.getWorldPosition === 'function') {
      // THREE.Object3D
      const v = target.getWorldPosition({ x: 0, y: 0, z: 0 })
      return [v.x, v.y, v.z]
    }
    if (target.position) return [target.position.x, target.position.y, target.position.z]
    if (typeof target === 'string') {
      const reg = (typeof window !== 'undefined' && window.__NAV_MARKERS__) || null
      if (reg && reg[target]) return toVec3(reg[target])
      const scene = (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || null
      const obj = scene && typeof scene.getObjectByName === 'function' ? scene.getObjectByName(target) : null
      if (obj && typeof obj.getWorldPosition === 'function') {
        const v = obj.getWorldPosition({ x: 0, y: 0, z: 0 })
        return [v.x, v.y, v.z]
      }
      return null
    }
    if ('x' in target && 'z' in target) return [target.x, target.y ?? 0, target.z]
    return null
  }

  _setWalk(moving) {
    // Every start and stop passes through here, so it is where speed is published. The
    // fade below is the older GLB route, which a loaded body graph zeroes out anyway.
    this._speedSink?.(moving ? this.speed : 0)
    const actions = (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__) || null
    if (!actions) return
    if (!this._walkAction) {
      for (const name of Object.keys(actions)) {
        if (/walk|locomotion|stride/i.test(name)) { this._walkAction = actions[name]; break }
      }
    }
    if (!this._walkAction) return
    try {
      if (moving && !this._walkPlaying) {
        this._walkAction.reset().fadeIn(0.2).play()
        this._walkPlaying = true
      } else if (!moving && this._walkPlaying) {
        this._walkAction.fadeOut(0.2)
        this._walkPlaying = false
      }
    } catch { /* mixer not ready — ignore */ }
  }

  _fire(type, data) {
    if (this._eventSink) { this._eventSink(type, data); return }
    const er = (typeof window !== 'undefined' && window.__EVENT_RUNTIME__) || null
    if (er && typeof er.fireEvent === 'function') er.fireEvent(type, data)
  }
}

function toVec3(p) {
  if (!p) return null
  if (Array.isArray(p)) return p.length >= 3 ? [p[0], p[1], p[2]] : (p.length === 2 ? [p[0], 0, p[1]] : null)
  if (typeof p === 'object' && 'x' in p && 'z' in p) return [p.x, p.y ?? 0, p.z]
  return null
}

function distXZ(a, b) {
  const dx = a[0] - b[0], dz = a[2] - b[2]
  return Math.sqrt(dx * dx + dz * dz)
}

export const movement = new MovementComponent()

if (typeof window !== 'undefined') {
  window.__MOVEMENT__ = {
    get state() { return { status: movement.status, path: movement.path, targetIndex: movement.targetIndex, speed: movement.speed } },
    moveToLocation: (p, o) => movement.moveToLocation(p, o),
    moveToActor: (t, o) => movement.moveToActor(t, o),
    moveToRandomPoint: (c, r, o) => movement.moveToRandomPoint(c, r, o),
    abort: () => movement.abort(),
  }
}
